import { BlurConfig, PipelineConfig } from '../config-types';
import { FrameData, LadonOutput } from '../generator';
import { logger } from '../logger';
import { compileShader, linkProgram } from '../util';

import { LadonRenderer } from './ladon-renderer';
import { WebGLRenderer } from './webgl-renderer';

export class BlurRenderer extends WebGLRenderer implements LadonRenderer {
    private program_1: WebGLProgram;
    private program_2: WebGLProgram;

    private framebuffer: WebGLFramebuffer;
    private transfer_texture: WebGLTexture;

    private u_blur: WebGLUniformLocation;

    private fragment_shader_common = `#version 300 es
precision highp float;

#define KERNEL_SIZE     ${(this.config.render as BlurConfig).kernelSize}
#define HORIZONTAL      vec2(1.0, 0.0)
#define VERTICAL        vec2(0.0, 1.0)

out vec4 outColor;

uniform sampler2D       u_image;
uniform sampler2D       u_mask;
uniform vec2            u_resolution;
uniform float           u_kernel[KERNEL_SIZE];

// Function to retrieve the mask value
float get_mask(vec2 st) {
    return texture(u_mask, st).r;
}

// Get the inverse of the masked area
float get_inv_mask(vec2 st) {
    return 1.0 - get_mask(st);
}

// Blurring function
vec3 blur(sampler2D src, vec2 uv, vec2 direction) {
    vec4 accum = vec4(0.0);
    vec2 step = direction / u_resolution;
    int offset = (KERNEL_SIZE - 1) / 2;

    float weightSum = 0.0;
    for (int i = 0; i < KERNEL_SIZE; i++) {
        vec2 sampleUV = uv + step * float(i - offset);
        vec4 sampleTex = texture(src, sampleUV);
        float weight = u_kernel[i];
        accum += sampleTex * weight;
        weightSum += weight;
    }

    weightSum = max(weightSum, 0.001); // Avoid division by zero
    return accum.rgb / weightSum;
}
`;

    private fragment_shader_pass_1_src =
        this.fragment_shader_common +
        `
void main() {
    vec2 st = gl_FragCoord.xy / u_resolution;
    outColor = vec4(blur(u_image, st, HORIZONTAL), 1.0);
}
`;

    public fragment_shader_pass_2_src =
        this.fragment_shader_common +
        `
uniform sampler2D   u_blur;

void main() {
    ${this.getUVCalculation()};

    vec3 blurred = blur(u_blur, st, VERTICAL);
    vec3 original = texture(u_image, st).rgb;
    float mask = get_inv_mask(st); // Use inverse mask to blur background, not user

    // Correct blending based on the mask
    vec3 color = mix(original, blurred, mask); // Ensure mask properly determines blur influence on background
    outColor = vec4(color, 1.0);
}
`;

    constructor(public output_canvas: HTMLCanvasElement, public readonly config: PipelineConfig) {
        super(output_canvas, config);

        if ((this.config.render as BlurConfig).kernelSize % 2 === 0) {
            logger.errorAndThrow('Kernel_size must be odd');
        }

        // program pass 1 setup
        {
            const vertex_shader = compileShader(
                this.gl,
                this.gl.VERTEX_SHADER,
                this.vertex_shader_src
            );
            const fragment_shader = compileShader(
                this.gl,
                this.gl.FRAGMENT_SHADER,
                this.fragment_shader_pass_1_src
            );

            this.program_1 = linkProgram(this.gl, vertex_shader, fragment_shader);
            this.gl.useProgram(this.program_1);

            const framebuffer = this.gl.createFramebuffer();
            if (!framebuffer) {
                logger.errorAndThrow('Error creating framebuffer');
            }
            this.framebuffer = framebuffer;

            const transfer_texture = this.gl.createTexture();
            if (!transfer_texture) {
                logger.errorAndThrow('Error creating output texture');
            }
            this.transfer_texture = transfer_texture;

            this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.transfer_texture);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_S,
                this.gl.CLAMP_TO_EDGE
            );
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_T,
                this.gl.CLAMP_TO_EDGE
            );
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.RGBA,
                this.config.input.width,
                this.config.input.height,
                0,
                this.gl.RGBA,
                this.gl.UNSIGNED_BYTE,
                null
            );
            this.gl.framebufferTexture2D(
                this.gl.FRAMEBUFFER,
                this.gl.COLOR_ATTACHMENT0,
                this.gl.TEXTURE_2D,
                this.transfer_texture,
                0
            );
            this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);

            this.setSharedUniforms(this.program_1);

            const u_kernel = this.gl.getUniformLocation(this.program_1, 'u_kernel');
            if (!u_kernel) {
                logger.errorAndThrow('Error finding "u_kernel" uniform');
            }
            this.gl.uniform1fv(u_kernel, this.getBlurKernel());
        }

        // program pass 2 setup
        {
            const vertex_shader = compileShader(
                this.gl,
                this.gl.VERTEX_SHADER,
                this.vertex_shader_src
            );
            const fragment_shader = compileShader(
                this.gl,
                this.gl.FRAGMENT_SHADER,
                this.fragment_shader_pass_2_src
            );

            this.program_2 = linkProgram(this.gl, vertex_shader, fragment_shader);
            this.gl.useProgram(this.program_2);

            const u_blur = this.gl.getUniformLocation(this.program_2, 'u_blur');
            if (!u_blur) {
                logger.errorAndThrow('Error finding "u_blur" uniform');
            }
            this.u_blur = u_blur;
            this.gl.activeTexture(this.gl.TEXTURE2);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.transfer_texture);
            this.gl.uniform1i(this.u_blur, 2);

            this.setSharedUniforms(this.program_2);

            const u_kernel = this.gl.getUniformLocation(this.program_2, 'u_kernel');
            if (!u_kernel) {
                logger.errorAndThrow('Error finding "u_kernel" uniform');
            }
            this.gl.uniform1fv(u_kernel, this.getBlurKernel());
        }
    }

    public async render(input: FrameData, mask?: LadonOutput): Promise<void> {
        this.uploadVideoStream(input);
        this.uploadMask(mask);

        // program pass 1 - blur in one direction & output to framebuffer
        this.gl.useProgram(this.program_1);

        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);
        this.gl.drawArrays(this.gl.TRIANGLES, 0, this.verts.length / 3);
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);

        // program pass 2 - blur in other direction & draw to screen
        this.gl.useProgram(this.program_2);
        this.gl.drawArrays(this.gl.TRIANGLES, 0, this.verts.length / 3);

        this.postDraw();
    }

    public async load(): Promise<void> {
        // empty
    }

    public destroy(): void {
        // empty
    }

    private getBlurKernel(): Float32Array {
        const kernel = [];
        let sum = 0;
        for (let i = 0; i < (this.config.render as BlurConfig).kernelSize; i++) {
            let w = i - ((this.config.render as BlurConfig).kernelSize - 1) / 2;
            w = Math.exp(-(w ** 2) / (this.config.render as BlurConfig).sigma ** 2);
            kernel.push(w);
            sum += w;
        }

        for (let i = 0; i < (this.config.render as BlurConfig).kernelSize; i++) {
            kernel[i] = kernel[i] / sum;
        }

        return new Float32Array(kernel);
    }
}
